/**
 * Plays synthesised phrases strictly in order while fetching the next ones in
 * the background, so there is no gap between sentences. Interruptible at any
 * time (the user talks over the assistant or ends the call).
 */

export interface SpeechQueueDeps<Audio> {
  /** Fetch audio for one phrase; may reject (then `fallback` is used). */
  synthesize(text: string, signal: AbortSignal): Promise<Audio>;
  /** Resolves when the audio finished playing; must reject/settle when the signal aborts. */
  play(audio: Audio, signal: AbortSignal): Promise<void>;
  /** Last resort when synthesis fails (e.g. the browser's own voice). */
  fallback?(text: string, signal: AbortSignal): Promise<void>;
  /** First sound of a run is about to play. */
  onStart?(): void;
  /** Everything queued has been played. */
  onIdle?(): void;
  onError?(error: unknown): void;
}

interface Item<Audio> {
  text: string;
  audio: Promise<Audio | null>;
}

export class SpeechQueue<Audio> {
  private items: Item<Audio>[] = [];
  private running = false;
  private controller = new AbortController();

  constructor(private readonly deps: SpeechQueueDeps<Audio>) {}

  get busy(): boolean {
    return this.running || this.items.length > 0;
  }

  enqueue(text: string): void {
    const signal = this.controller.signal;
    const audio = this.deps.synthesize(text, signal).catch((error: unknown) => {
      if (!signal.aborted) this.deps.onError?.(error);
      return null; // play() is skipped and `fallback` speaks the phrase instead
    });
    this.items.push({ text, audio });
    if (!this.running) void this.run();
  }

  /** Drops everything queued and silences what is playing. */
  interrupt(): void {
    this.controller.abort();
    this.controller = new AbortController();
    this.items = [];
  }

  private async run(): Promise<void> {
    this.running = true;
    const signal = this.controller.signal;
    let started = false;
    try {
      while (this.items.length > 0 && !signal.aborted) {
        const item = this.items.shift()!;
        const audio = await item.audio;
        if (signal.aborted) break;
        if (!started) {
          started = true;
          this.deps.onStart?.();
        }
        try {
          if (audio !== null) await this.deps.play(audio, signal);
          else if (this.deps.fallback) await this.deps.fallback(item.text, signal);
        } catch (error) {
          if (!signal.aborted) this.deps.onError?.(error);
        }
      }
    } finally {
      this.running = false;
    }
    if (signal.aborted) {
      // `interrupt()` ran. Phrases queued since then belong to the new run and still need a player.
      if (this.items.length > 0) void this.run();
      return;
    }
    this.deps.onIdle?.();
  }
}
